import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { platformConfigApi, type ConfigDomain } from '../api/services/platform-config';

/**
 * Generic hook for platform configuration pages.
 *
 * Fetches config for a given domain and provides a save mutation.
 *
 * `config` is undefined until the read succeeds, and stays undefined when it
 * fails. The hook took a `defaultValues` argument and returned it in place of
 * the document (`data ?? defaultValues`), so a pending or failed read rendered
 * client literals as the stored configuration. A caller now decides what an
 * absent document looks like, and the answer is never a literal.
 */
export function usePlatformConfig<T extends Record<string, unknown>>(domain: ConfigDomain) {
  const queryClient = useQueryClient();
  const queryKey = ['platform-config', domain];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => platformConfigApi.get<T>(domain),
    retry: 1,
  });

  const mutation = useMutation({
    mutationFn: (updated: T) => platformConfigApi.update<T>(domain, updated),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKey, result);
    },
  });

  return {
    config: data,
    isLoading,
    error,
    save: mutation.mutate,
    isSaving: mutation.isPending,
    saveError: mutation.error,
  };
}
